import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Navigate, Outlet } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { GlobalCanonical } from "@/components/GlobalCanonical";
import { GlobalStructuredData } from "@/components/GlobalStructuredData";
import type { RouteRecord } from "vite-react-ssg";

// Homepage loaded eagerly (critical path)
import Index from "./pages/Index";

// All other pages lazy-loaded for code splitting
const OurFirmValues = lazy(() => import("./pages/OurFirmValues"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Services = lazy(() => import("./pages/Services"));
const AllReviews = lazy(() => import("./pages/AllReviews"));
const Consultation = lazy(() => import("./pages/Consultation"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));

const TaxAccountant = lazy(() => import("./pages/TaxAccountant"));
const TaxPlanning = lazy(() => import("./pages/TaxPlanning"));
const BusinessAdvisory = lazy(() => import("./pages/BusinessAdvisory"));
const ConstructionTradesAccounting = lazy(() => import("./pages/ConstructionTradesAccounting"));
const MedicalPracticeAccounting = lazy(() => import("./pages/MedicalPracticeAccounting"));
const ITConsultingGovConAccounting = lazy(() => import("./pages/ITConsultingGovConAccounting"));
const LawFirmProfessionalServicesAccounting = lazy(() => import("./pages/LawFirmProfessionalServicesAccounting"));
const RealEstateInvestorLandlordAccounting = lazy(() => import("./pages/RealEstateInvestorLandlordAccounting"));
const FranchiseAccounting = lazy(() => import("./pages/FranchiseAccounting"));
const MedSpaWellnessAccounting = lazy(() => import("./pages/MedSpaWellnessAccounting"));
const FarmAccounting = lazy(() => import("./pages/FarmAccounting"));
const GlobalMobilityExpatTax = lazy(() => import("./pages/GlobalMobilityExpatTax"));
const SmallBusinessBookkeepingNova = lazy(() => import("./pages/SmallBusinessBookkeepingNova"));
const TaxFilingLanding = lazy(() => import("./pages/TaxFilingLanding"));
const TaxFilingLandingB = lazy(() => import("./pages/TaxFilingLandingB"));
const SmallBusinessTaxPreparationNova = lazy(() => import("./pages/SmallBusinessTaxPreparationNova"));
const SmallBusinessTaxPlanningNova = lazy(() => import("./pages/SmallBusinessTaxPlanningNova"));
const BusinessAdvisoryNova = lazy(() => import("./pages/BusinessAdvisoryNova"));
const FractionalCFOServices = lazy(() => import("./pages/FractionalCFOServices"));
const BusinessTaxPreparationManassas = lazy(() => import("./pages/BusinessTaxPreparationManassas"));
const HomeServicesLanding = lazy(() => import("./pages/HomeServicesLanding"));

// City Landing Pages
const CpaArlingtonVa = lazy(() => import("./pages/CpaArlingtonVa"));
const CpaFairfaxVa = lazy(() => import("./pages/CpaFairfaxVa"));
const AccountantWashingtonDc = lazy(() => import("./pages/AccountantWashingtonDc"));
const CpaAlexandriaVa = lazy(() => import("./pages/CpaAlexandriaVa"));
const CpaRestonVa = lazy(() => import("./pages/CpaRestonVa"));

// Tax Filing Service Pages
const TaxReturnPreparation = lazy(() => import("./pages/tax-filing/TaxReturnPreparation"));
const PersonalTaxReturns = lazy(() => import("./pages/tax-filing/PersonalTaxReturns"));
const JointTaxReturnPreparation = lazy(() => import("./pages/tax-filing/JointTaxReturnPreparation"));
const TaxExtensionFiling = lazy(() => import("./pages/tax-filing/TaxExtensionFiling"));
const LLCBusinessTaxFiling = lazy(() => import("./pages/tax-filing/LLCBusinessTaxFiling"));
const SCorpTaxReturn = lazy(() => import("./pages/tax-filing/SCorpTaxReturn"));
const PartnershipTaxReturn = lazy(() => import("./pages/tax-filing/PartnershipTaxReturn"));
const CCorpTaxReturn = lazy(() => import("./pages/tax-filing/CCorpTaxReturn"));
const MultiStateBusinessTaxFiling = lazy(() => import("./pages/tax-filing/MultiStateBusinessTaxFiling"));
const SalesTaxFiling = lazy(() => import("./pages/tax-filing/SalesTaxFiling"));
const PayrollTaxFilings = lazy(() => import("./pages/tax-filing/PayrollTaxFilings"));
const IRSRepresentation = lazy(() => import("./pages/tax-filing/IRSRepresentation"));
const IRSAuditRepresentation = lazy(() => import("./pages/tax-filing/IRSAuditRepresentation"));
const TaxProblemConsulting = lazy(() => import("./pages/tax-filing/TaxProblemConsulting"));

// Tax Planning Service Pages
const YearEndTaxPlanning = lazy(() => import("./pages/tax-planning/YearEndTaxPlanning"));
const QuarterlyEstimatedTaxPlanning = lazy(() => import("./pages/tax-planning/QuarterlyEstimatedTaxPlanning"));
const EntityStructureOwnerCompensation = lazy(() => import("./pages/tax-planning/EntityStructureOwnerCompensation"));
const SCorpTaxPlanning = lazy(() => import("./pages/tax-planning/SCorpTaxPlanning"));
const LLCTaxPlanning = lazy(() => import("./pages/tax-planning/LLCTaxPlanning"));
const RetirementDeductionTaxPlanning = lazy(() => import("./pages/tax-planning/RetirementDeductionTaxPlanning"));
const MultiStateDCTaxPlanning = lazy(() => import("./pages/tax-planning/MultiStateDCTaxPlanning"));

// Business Advisory Service Pages
const TaxStructureEntityPlanning = lazy(() => import("./pages/business-advisory/TaxStructureEntityPlanning"));
const CashFlowDebtServiceModeling = lazy(() => import("./pages/business-advisory/CashFlowDebtServiceModeling"));
const PostAcquisitionIntegration = lazy(() => import("./pages/business-advisory/PostAcquisitionIntegration"));
const ProfitabilityReviews = lazy(() => import("./pages/business-advisory/ProfitabilityReviews"));
const CashFlowPlanning = lazy(() => import("./pages/business-advisory/CashFlowPlanning"));
const OwnerDistributionsPlanning = lazy(() => import("./pages/business-advisory/OwnerDistributionsPlanning"));
const GrowthStrategyAdvisory = lazy(() => import("./pages/business-advisory/GrowthStrategyAdvisory"));

// Bookkeeping Service Pages
const MonthlyBookkeepingReconciliations = lazy(() => import("./pages/bookkeeping/MonthlyBookkeepingReconciliations"));
const BookkeepingCleanupCatchUp = lazy(() => import("./pages/bookkeeping/BookkeepingCleanupCatchUp"));
const FinancialStatementPreparation = lazy(() => import("./pages/bookkeeping/FinancialStatementPreparation"));
const FinancialAnalysisReporting = lazy(() => import("./pages/bookkeeping/FinancialAnalysisReporting"));
const BudgetingForecastingCashFlow = lazy(() => import("./pages/bookkeeping/BudgetingForecastingCashFlow"));
const QuickBooksConsultingSetup = lazy(() => import("./pages/bookkeeping/QuickBooksConsultingSetup"));
const QuickBooksOnlineSetupSupport = lazy(() => import("./pages/bookkeeping/QuickBooksOnlineSetupSupport"));
const PayrollSetupProcessing = lazy(() => import("./pages/bookkeeping/PayrollSetupProcessing"));
const NewBusinessSetupAccounting = lazy(() => import("./pages/bookkeeping/NewBusinessSetupAccounting"));

// Initialize React Query client
const queryClient = new QueryClient();

// Suspense wrapper for lazy routes
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div className="min-h-screen" style={{ backgroundColor: 'hsl(40 30% 97%)' }} />}>
    {children}
  </Suspense>
);

// Layout component that wraps all routes
const Layout = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <ScrollToTop />
      <GlobalCanonical />
      <GlobalStructuredData />
      <Outlet />
    </TooltipProvider>
  </QueryClientProvider>
);

// Helper to wrap lazy components
const L = (Component: React.LazyExoticComponent<any>) => (
  <LazyRoute><Component /></LazyRoute>
);

// Routes configuration for SSG
export const routes: RouteRecord[] = [
  {
    path: "/",
    element: <Layout />,
    children: [
      { index: true, element: <Index /> },
      { path: "about", element: L(OurFirmValues) },
      { path: "our-team", element: <Navigate to="/about" replace /> },
      { path: "services", element: L(Services) },
      { path: "reviews", element: L(AllReviews) },
      { path: "consultation", element: L(Consultation) },
      { path: "privacy-policy", element: L(PrivacyPolicy) },
      { path: "terms-and-conditions", element: L(TermsAndConditions) },
      { path: "terms-of-service", element: <Navigate to="/terms-and-conditions" replace /> },
      { path: "terms-of-service.php", element: <Navigate to="/terms-and-conditions" replace /> },
      { path: "small-business-bookkeeping-manassas-va", element: <Navigate to="/small-business-bookkeeping-northern-virginia-dc" replace /> },
      { path: "tax-accountant-manassas-va", element: L(TaxAccountant) },
      { path: "small-business-tax-planning-manassas-va", element: L(TaxPlanning) },
      { path: "business-advisory-ma-manassas-va", element: L(BusinessAdvisory) },
      { path: "construction-trades-home-services-accounting-northern-virginia-dc", element: L(ConstructionTradesAccounting) },
      { path: "medical-practice-accounting-northern-virginia-dc", element: L(MedicalPracticeAccounting) },
      { path: "it-consulting-govcon-accounting-northern-virginia-dc", element: L(ITConsultingGovConAccounting) },
      { path: "law-firm-professional-services-accounting-northern-virginia-dc", element: L(LawFirmProfessionalServicesAccounting) },
      { path: "real-estate-investor-landlord-accounting-northern-virginia-dc", element: L(RealEstateInvestorLandlordAccounting) },
      { path: "franchise-accounting-northern-virginia-dc", element: L(FranchiseAccounting) },
      { path: "med-spa-wellness-clinic-accounting-northern-virginia-dc", element: L(MedSpaWellnessAccounting) },
      { path: "farm-agriculture-accounting-northern-virginia-dc", element: L(FarmAccounting) },
      { path: "global-mobility-expat-international-tax-northern-virginia-dc", element: L(GlobalMobilityExpatTax) },
      { path: "small-business-bookkeeping-northern-virginia-dc", element: L(SmallBusinessBookkeepingNova) },
      { path: "small-business-tax-preparation-northern-virginia-dc", element: L(SmallBusinessTaxPreparationNova) },
      { path: "small-business-tax-planning-northern-virginia-dc", element: L(SmallBusinessTaxPlanningNova) },
      { path: "business-advisory-cfo-services-northern-virginia-dc", element: L(BusinessAdvisoryNova) },
      { path: "fractional-cfo-services-northern-virginia-dc", element: L(FractionalCFOServices) },
      { path: "business-tax-preparation-manassas-va", element: L(BusinessTaxPreparationManassas) },
      { path: "medical-practices", element: L(TaxFilingLanding) },
      { path: "tax-filing", element: L(TaxFilingLandingB) },
      { path: "home-services", element: L(HomeServicesLanding) },
      // City Landing Pages
      { path: "cpa-arlington-va", element: L(CpaArlingtonVa) },
      { path: "cpa-fairfax-va", element: L(CpaFairfaxVa) },
      { path: "accountant-washington-dc", element: L(AccountantWashingtonDc) },
      { path: "cpa-alexandria-va", element: L(CpaAlexandriaVa) },
      { path: "cpa-reston-va", element: L(CpaRestonVa) },
      // Tax Filing Service Child Pages
      { path: "tax-filing-manassas-va/tax-return-preparation", element: L(TaxReturnPreparation) },
      { path: "tax-filing-manassas-va/personal-tax-returns", element: L(PersonalTaxReturns) },
      { path: "tax-filing-manassas-va/joint-tax-return-preparation", element: L(JointTaxReturnPreparation) },
      { path: "tax-filing-manassas-va/tax-extension-filing", element: L(TaxExtensionFiling) },
      { path: "tax-filing-manassas-va/llc-business-tax-filing", element: L(LLCBusinessTaxFiling) },
      { path: "tax-filing-manassas-va/s-corp-tax-return-1120s", element: L(SCorpTaxReturn) },
      { path: "tax-filing-manassas-va/partnership-tax-return-1065", element: L(PartnershipTaxReturn) },
      { path: "tax-filing-manassas-va/c-corp-tax-return-1120", element: L(CCorpTaxReturn) },
      { path: "tax-filing-manassas-va/multi-state-business-tax-filing", element: L(MultiStateBusinessTaxFiling) },
      { path: "tax-filing-manassas-va/sales-tax-filing-returns", element: L(SalesTaxFiling) },
      { path: "tax-filing-manassas-va/payroll-tax-filings-941-940", element: L(PayrollTaxFilings) },
      { path: "tax-filing-manassas-va/irs-representation", element: L(IRSRepresentation) },
      { path: "tax-filing-manassas-va/irs-audit-representation", element: L(IRSAuditRepresentation) },
      { path: "tax-filing-manassas-va/tax-problem-consulting", element: L(TaxProblemConsulting) },
      // Tax Planning Service Child Pages
      { path: "tax-planning-manassas-va/year-end-tax-planning", element: L(YearEndTaxPlanning) },
      { path: "tax-planning-manassas-va/quarterly-estimated-tax-planning", element: L(QuarterlyEstimatedTaxPlanning) },
      { path: "tax-planning-manassas-va/entity-structure-owner-compensation", element: L(EntityStructureOwnerCompensation) },
      { path: "tax-planning-manassas-va/s-corp-tax-planning", element: L(SCorpTaxPlanning) },
      { path: "tax-planning-manassas-va/llc-tax-planning", element: L(LLCTaxPlanning) },
      { path: "tax-planning-manassas-va/retirement-deduction-tax-planning", element: L(RetirementDeductionTaxPlanning) },
      { path: "tax-planning-manassas-va/multi-state-dc-tax-planning", element: L(MultiStateDCTaxPlanning) },
      // Business Advisory Service Child Pages
      { path: "business-advisory-ma-manassas-va/tax-structure-entity-planning", element: L(TaxStructureEntityPlanning) },
      { path: "business-advisory-ma-manassas-va/cash-flow-debt-service-modeling", element: L(CashFlowDebtServiceModeling) },
      { path: "business-advisory-ma-manassas-va/post-acquisition-integration", element: L(PostAcquisitionIntegration) },
      { path: "business-advisory-ma-manassas-va/profitability-reviews", element: L(ProfitabilityReviews) },
      { path: "business-advisory-ma-manassas-va/cash-flow-planning", element: L(CashFlowPlanning) },
      { path: "business-advisory-ma-manassas-va/owner-distributions-planning", element: L(OwnerDistributionsPlanning) },
      { path: "business-advisory-ma-manassas-va/growth-strategy-advisory", element: L(GrowthStrategyAdvisory) },
      // 301 Redirects from old PHP site (client-side fallbacks for SPA navigation)
      { path: "about.php", element: <Navigate to="/about" replace /> },
      { path: "services.php", element: <Navigate to="/services" replace /> },
      { path: "taxprep.php", element: <Navigate to="/tax-accountant-manassas-va" replace /> },
      { path: "taxplanning.php", element: <Navigate to="/services" replace /> },
      { path: "irs-backtaxes.php", element: <Navigate to="/tax-filing-manassas-va/tax-problem-consulting" replace /> },
      { path: "irs-nonfiledreturns.php", element: <Navigate to="/tax-filing-manassas-va/tax-problem-consulting" replace /> },
      { path: "forensic_accounting.php", element: <Navigate to="/business-advisory-ma-manassas-va" replace /> },
      { path: "realestate.php", element: <Navigate to="/real-estate-investor-landlord-accounting-northern-virginia-dc" replace /> },
      { path: "quickbookstips.php", element: <Navigate to="/services" replace /> },
      { path: "sitemap.php", element: <Navigate to="/" replace /> },
      { path: "email-notice.php", element: <Navigate to="/privacy-policy" replace /> },
      { path: "email-notice", element: <Navigate to="/privacy-policy" replace /> },
      { path: "frequently-asked-questions.php", element: <Navigate to="/" replace /> },
      // Bookkeeping Service Child Pages
      { path: "bookkeeping-northern-virginia-dc/monthly-bookkeeping-reconciliations", element: L(MonthlyBookkeepingReconciliations) },
      { path: "bookkeeping-northern-virginia-dc/bookkeeping-cleanup-catch-up", element: L(BookkeepingCleanupCatchUp) },
      { path: "bookkeeping-northern-virginia-dc/financial-statement-preparation", element: L(FinancialStatementPreparation) },
      { path: "bookkeeping-northern-virginia-dc/financial-analysis-reporting", element: L(FinancialAnalysisReporting) },
      { path: "bookkeeping-northern-virginia-dc/budgeting-forecasting-cash-flow", element: L(BudgetingForecastingCashFlow) },
      { path: "bookkeeping-northern-virginia-dc/quickbooks-consulting-setup", element: L(QuickBooksConsultingSetup) },
      { path: "bookkeeping-northern-virginia-dc/quickbooks-online-setup-support", element: L(QuickBooksOnlineSetupSupport) },
      { path: "bookkeeping-northern-virginia-dc/payroll-setup-processing", element: L(PayrollSetupProcessing) },
      { path: "bookkeeping-northern-virginia-dc/new-business-setup-accounting", element: L(NewBusinessSetupAccounting) },
      // Catch-all route
      { path: "*", element: L(NotFound) },
    ],
  },
];
